import { useCallback, useState, useEffect } from 'react';
import { Button, DropdownOptions, secondaryButtonClassName } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { useUserStore } from '../../../auth/store/userStore';
import { useAuth } from '../../../auth/hooks/useAuth';
import { transferOwnership } from '../../services/profileService';
import { getUsers } from '../../../team/services/teamService';
import { handleApiError, showToast } from '../../../../shared/services/toast';
import { hasAccountOwnerRole } from '../../../../shared/constants/roles';
import { ConfirmationDialog } from '@shared/components/ConfirmationDialog';
import { sectionHeading, sectionDescription } from '../profileStyles';

export function TransferOwnershipSection({ dataTestId }) {
  const baseTestId = dataTestId || 'profile-transfer-section';
  const { logout } = useAuth();
  const { profile } = useUserStore();
  const clearProfile = useUserStore((state) => state.clearProfile);
  const currentUserId = profile?.personalInfo?.userId ?? profile?.userId ?? null;
  const userRoles = profile?.roles || [];
  const isAccountOwner = hasAccountOwnerRole(userRoles);

  const [open, setOpen] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');

  // Fetch users when component mounts
  useEffect(() => {
    if (!isAccountOwner) return;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await getUsers({
          filters: [],
          sorts: [{ field: 'Name', direction: 'asc' }],
          pageNumber: 1,
          pageSize: 100
        });
        
        if (response?.data?.users) {
          // Filter out current user from the list
          const filteredUsers = response.data.users.filter(
            user => user.userId !== currentUserId
          );
          setUsers(filteredUsers);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    void fetchUsers();
  }, [currentUserId, isAccountOwner]);

  // Prevent modal from closing when clicking outside during transfer
  const handleOpenChange = (newOpen) => {
    if (isTransferring) return;
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedUserId('');
      setSelectedUserName('');
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    const user = users.find(u => u.userId.toString() === userId);
    setSelectedUserName(user?.name || '');
  };

  const onConfirmTransfer = useCallback(async () => {
    /* istanbul ignore next -- defensive: the Transfer button (and thus the confirm dialog) is disabled whenever selectedUserId is empty, so this guard is unreachable via the UI */
    if (!selectedUserId) {
      showToast.error('Please select a user to transfer ownership to.');
      return;
    }

    setIsTransferring(true);
    try {
      const response = await transferOwnership(Number.parseInt(selectedUserId, 10));
      const message = response?.message || 'Ownership transferred successfully.';
      showToast.success(message);
      setOpen(false);
      
      // Clear cached profile and roles from store immediately
      clearProfile();
      
      // Logout after successful transfer to force fresh JWT token with new roles
      await logout();
    } catch (err) {
      handleApiError(err, 'Failed to transfer ownership');
    } finally {
      setIsTransferring(false);
    }
  }, [selectedUserId, logout, clearProfile]);

  const hasOtherUsers = users.length > 0;

  // Only render for Account Owners. Placed after all hooks so hook order stays
  // unconditional (rules-of-hooks / S6440).
  if (!isAccountOwner) {
    return null;
  }

  let selectPlaceholder = 'No other users';
  if (loadingUsers) {
    selectPlaceholder = 'Loading users...';
  } else if (hasOtherUsers) {
    selectPlaceholder = 'Select new owner';
  }

  return (
    <div className="space-y-4 pt-6 border-t border-line" data-testid={baseTestId}>
      <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Transfer Ownership</h3>
      <p className={sectionDescription} data-testid={`${baseTestId}-description`}>
        Transfer account ownership to another user. You will be assigned the Billing Admin role.
      </p>

      {/* Stacked on phones; side by side from `sm`. `items-start`, not `items-center`: inside the
          profile <form> the dropdown also renders a hidden native <select>, which its `space-y-2`
          pushes 8px down, so centring would sit the button 4px below the trigger. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        {/* Shared DropdownOptions — the same control as Main Contact just above, with the field
            outline every other form field has (it used the faint card border before). */}
        <DropdownOptions
          id={`${baseTestId}-user-select`}
          value={selectedUserId}
          onValueChange={handleUserSelect}
          options={users.map((user) => ({ value: user.userId.toString(), label: user.name }))}
          disabled={loadingUsers || !hasOtherUsers}
          placeholder={selectPlaceholder}
          className="w-full sm:w-[280px]"
          dataTestId={`${baseTestId}-user-select`}
        />

        <Button
          type="button"
          variant="outline"
          // The dashboard's "Manage" look (shared), so in-content actions match across the app.
          // h-11 to sit level with the 44px user dropdown beside it in this row.
          className={cn(secondaryButtonClassName, 'h-11 w-full sm:w-auto')}
          onClick={() => setOpen(true)}
          disabled={!selectedUserId || loadingUsers}
          data-testid={`${baseTestId}-submit-button`}
        >
          Transfer Ownership
        </Button>
      </div>

      <ConfirmationDialog
        isOpen={open}
        title="Confirm ownership transfer?"
        description={`You are about to transfer account ownership to ${selectedUserName}.`}
        additionalInfo="You will be logged out of the system after this action."
        cancelLabel="Cancel"
        confirmLabel="Transfer"
        variant="primary"
        isLoading={isTransferring}
        onCancel={() => handleOpenChange(false)}
        onConfirm={onConfirmTransfer}
        dataTestId={`${baseTestId}-confirmation`}
      />
    </div>
  );
}
